import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus } from 'lucide-react'

import {
  Button,
  EmptySearchResult,
  SubtitleText,
  TitleText,
} from '@/components'
import InputSearch from '@/components/InputSearch'
import { Table } from '@/components/Table'
import {
  getUserMappingListColumns,
  UserMappingAction,
  UserMappingSource,
  useUserMappingList,
} from '@/features/user-mapping'
import {
  DeleteUserMappingDialog,
  UserMappingDialog,
} from '@/features/user-mapping/components'
import { useUserMappingStore } from '@/features/user-mapping/store'
import { ResponseUserMapping } from '@/schema'
import { SortOrder } from '@/types/common'
import { DEFAULT_PAGE, SortDirection, TABLE_PAGE_SIZE } from '@/utils/constants'

const UserMappingPage = () => {
  const { t } = useTranslation()
  const {
    setEditingUserMapping,
    setDialogOpen,
    setDeletingUserMapping,
    setDeleteDialogOpen,
  } = useUserMappingStore()

  // Local state for search, pagination and sort
  const [searchText, setSearchText] = useState('')
  const [page, setPage] = useState(0)
  const [sort, setSort] = useState<{
    column: keyof ResponseUserMapping | null
    direction: SortOrder
  }>({
    column: 'vista_username',
    direction: SortDirection.ASC,
  })

  const { data: { data = [], pagination } = {}, isLoading } =
    useUserMappingList({
      search: searchText,
      page,
      rowsPerPage: TABLE_PAGE_SIZE,
      sort: {
        column: sort.column as keyof ResponseUserMapping,
        direction: sort.direction,
      },
      source: UserMappingSource.MAPPED_ONLY,
    })

  const noDataMessage = useMemo(() => {
    if (searchText) {
      return <EmptySearchResult translationKey="userMapping" />
    }
    return null
  }, [searchText])

  // Handle action clicks (edit/delete)
  const handleActionClick = useCallback(
    (action: UserMappingAction, item: ResponseUserMapping) => {
      if (action === 'edit') {
        setEditingUserMapping(item)
        setDialogOpen(true)
      } else if (action === 'delete') {
        setDeletingUserMapping(item)
        setDeleteDialogOpen(true)
      }
    },
    [
      setEditingUserMapping,
      setDialogOpen,
      setDeletingUserMapping,
      setDeleteDialogOpen,
    ]
  )

  const handleSortChange = useCallback(
    (colId: keyof ResponseUserMapping | null, dir: SortOrder) => {
      if (colId === null && dir === null) {
        setSort({ column: null, direction: null })
        return
      }

      if (colId && dir) {
        setSort({ column: colId, direction: dir })
      }
    },
    []
  )

  const columns = useMemo(
    () => getUserMappingListColumns(handleActionClick, t),
    [handleActionClick, t]
  )

  return (
    <div className="space-y-6">
      <div className="flex justify-between gap-4">
        <div className="flex flex-col gap-1">
          <TitleText>{t('userMapping.page.title')}</TitleText>
          <SubtitleText>{t('userMapping.page.description')}</SubtitleText>
        </div>
        <Button
          className="px-3.5 py-2.5 border border-solid border-transparent h-fit text-sm min-w-[120px]"
          onClick={() => setDialogOpen(true)}
        >
          <div className="flex items-center gap-1">
            <Plus size={20} />
            {t('userMapping.actions.addNew')}
          </div>
        </Button>
      </div>
      <InputSearch
        searchText={searchText}
        setSearchText={setSearchText}
        placeholder={t('userMapping.filters.search')}
      />
      <Table
        columns={columns}
        data={data}
        isLoading={isLoading}
        roundedTop
        page={(pagination?.page ?? DEFAULT_PAGE) - 1}
        totalPages={pagination?.total_pages ?? 0}
        onPageChange={setPage}
        orderBy={sort.column as keyof ResponseUserMapping}
        order={sort.direction}
        onSortChange={handleSortChange}
        onRowClick={(row) => handleActionClick('edit', row)}
        noDataMessage={noDataMessage}
      />
      <UserMappingDialog />
      <DeleteUserMappingDialog page={page} onPageChange={setPage} />
    </div>
  )
}

export default UserMappingPage
